import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Check, X, Lock } from "lucide-react";
import api, { formatApiError } from "../../api";
import { Field, Select } from "../common";
import SectionShell, { FieldGroup, FieldGroups } from "./SectionShell";
import { useSectionForm } from "./useSectionForm";

const CAPABILITIES = [
  "view_financials", "manage_deals", "manage_projects",
  "invite_members", "set_visibility", "reassign_owner", "view_all_reports",
];
const EDITABLE_ROLES = ["user", "guest"];
const FIXED_ROLES = ["admin", "manager"];

/**
 * The capability matrix, plus the org-wide default visibility for new deals and
 * projects (D5).
 *
 * Admin and manager columns are read-only on purpose and rendered as glyphs
 * rather than disabled checkboxes: a greyed-out checkbox looks like something
 * that failed to load, while a lock in the header says "this is fixed by
 * design". `manage_users` and `configure_permissions` are deliberately absent —
 * those are admin-only and not in the matrix at all.
 */
export default function PermissionSettings() {
  const { t } = useTranslation();
  const caps = useSectionForm({ path: "/settings/capabilities" });

  // Default visibility lives on the general /settings record and is a single
  // enum, so it saves on change rather than through the bar -- one dropdown
  // does not need a dirty state, and an optimistic write with a rollback is
  // less machinery than a second save cycle on the same page.
  const [visibility, setVisibility] = useState(null);
  const [visError, setVisError] = useState("");

  useEffect(() => {
    api.get("/settings")
      .then((r) => setVisibility(r.data.default_visibility || "public"))
      .catch((e) => setVisError(formatApiError(e.response?.data?.detail) || e.message));
  }, []);

  const saveVisibility = async (value) => {
    const previous = visibility;
    setVisibility(value);
    setVisError("");
    try {
      await api.put("/settings", { default_visibility: value });
    } catch (e) {
      setVisibility(previous);
      setVisError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggle = (role, cap) => caps.setForm((prev) => ({
    ...prev,
    [role]: { ...prev[role], [cap]: !prev[role][cap] },
  }));

  return (
    <SectionShell
      icon={ShieldCheck} tone="success"
      title={t("capabilities.title")} desc={t("capabilities.desc")}
      error={caps.error || visError}
      dirty={caps.dirty} saving={caps.saving} saved={caps.saved}
      onSave={caps.save} onDiscard={caps.discard}>

      <FieldGroups>
        <FieldGroup label={t("capabilities.visibilityGroup")}
          hint={t("capabilities.visibilityHint")}>
          <div className="max-w-xs">
            <Field label={t("capabilities.defaultVisibility")}>
              <Select data-testid="default-visibility-select" value={visibility || "public"}
                disabled={visibility === null} onChange={(e) => saveVisibility(e.target.value)}>
                <option value="public">{t("capabilities.public")}</option>
                <option value="private">{t("capabilities.private")}</option>
              </Select>
            </Field>
          </div>
        </FieldGroup>

        <FieldGroup label={t("capabilities.matrixGroup")} hint={t("capabilities.matrixHint")}>
          {!caps.form ? (
            <p className="text-sm text-muted">{t("common.loading")}</p>
          ) : (
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-sm border-separate border-spacing-0"
                data-testid="capability-matrix">
                <thead>
                  <tr>
                    <th className="text-left font-medium text-muted text-xs uppercase tracking-wider py-2.5 pr-3 border-b border-border">
                      {t("capabilities.capability")}
                    </th>
                    {FIXED_ROLES.map((role) => (
                      <th key={role}
                        className="py-2.5 px-3 border-b border-border font-medium text-muted text-xs uppercase tracking-wider whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <Lock size={10} /> {t(`roles.${role}`)}
                        </span>
                      </th>
                    ))}
                    {EDITABLE_ROLES.map((role) => (
                      <th key={role}
                        className="py-2.5 px-3 border-b border-border font-medium text-txt text-xs uppercase tracking-wider whitespace-nowrap">
                        {t(`roles.${role}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CAPABILITIES.map((cap) => (
                    <tr key={cap} className="group">
                      <td className="py-2.5 pr-3 border-b border-border group-hover:bg-surface transition-colors">
                        <span className="font-medium">{t(`capabilities.${cap}`)}</span>
                        {cap === "reassign_owner" && (
                          <span className="block text-xs text-muted">{t("capabilities.reassignOwnerNote")}</span>
                        )}
                      </td>
                      {FIXED_ROLES.map((role) => (
                        <td key={role}
                          className="text-center py-2.5 px-3 border-b border-border group-hover:bg-surface transition-colors">
                          {caps.form[role][cap]
                            ? <Check size={15} className="inline text-success" />
                            : <X size={15} className="inline text-muted opacity-50" />}
                        </td>
                      ))}
                      {EDITABLE_ROLES.map((role) => (
                        <td key={role}
                          className="text-center py-2.5 px-3 border-b border-border group-hover:bg-surface transition-colors">
                          <input type="checkbox" className="accent-primary cursor-pointer"
                            data-testid={`cap-${role}-${cap}`}
                            aria-label={`${t(`capabilities.${cap}`)} — ${t(`roles.${role}`)}`}
                            checked={!!caps.form[role][cap]}
                            onChange={() => toggle(role, cap)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </FieldGroup>
      </FieldGroups>
    </SectionShell>
  );
}
